import React from "react";
import PageLayout, { SectionReveal } from "../components/PageLayout";
import PageHero from "../components/PageHero";
import PageBanner from "../components/PageBanner";
import { ShoppingCart, Headphones, CalendarCheck, Stethoscope, Building2, ArrowRight } from "lucide-react";

const TemplatesPage = () => {
  const templates = [
    {
      icon: ShoppingCart,
      title: "E-Ticaret Sipariş Takibi",
      desc: "Müşterilerin sipariş durumunu sorgulayabilir, iptal ve iade taleplerini sesli olarak işleyebilirsiniz. Canlı stok ve kargo bilgisi entegrasyonu dahil.",
      useCases: ["Sipariş durumu sorgulama", "İade/iade talep oluşturma", "Kargo takip numarası paylaşma", "Stok bilgisi sorgulama"],
      popular: true,
    },
    {
      icon: CalendarCheck,
      title: "Randevu Yönetimi",
      desc: "Kuaför, klinik, danışmanlık gibi işletmeler için uçtan uca randevu asistanı. Müsaitlik kontrolü, randevu oluşturma, iptal ve hatırlatma.",
      useCases: ["Takvimde müsaitlik sorgulama", "Randevu oluşturma / iptal", "Otomatik SMS/WhatsApp hatırlatma", "Bekleme listesi yönetimi"],
      popular: true,
    },
    {
      icon: Headphones,
      title: "Çağrı Merkezi Ön Karşılama",
      desc: "Gelen çağrıları akıllıca karşılayan, müşteriyi doğru departmana yönlendiren IVR benzeri sesli menü sistemi.",
      useCases: ["Doğal dilde menü navigasyonu", "Departman bazlı yönlendirme", "Müşteri doğrulama (TCKN, müşteri no)", "Yoğunluk anında geri arama planlama"],
    },
    {
      icon: Stethoscope,
      title: "Sağlık Danışmanlığı",
      desc: "Hastane ve klinikler için ön triyaj, randevu, sonuç bilgilendirme ve genel sağlık danışmanlığı senaryosu.",
      useCases: ["Semptom bazlı ön değerlendirme", "Tahlil sonucu bilgilendirme", "Bölüm ve doktor yönlendirme", "MHRS entegrasyonu"],
    },
    {
      icon: Building2,
      title: "Banka / Finans Asistanı",
      desc: "Bakiye sorgulama, havale/EFT işlemleri, kredi başvurusu takibi ve yatırım danışmanlığı gibi finansal senaryolar.",
      useCases: ["Hesap bakiyesi ve hareketleri", "Havale / EFT talimatı", "Kredi kartı işlemleri", "Döviz kuru sorgulama"],
    },
    {
      icon: Building2,
      title: "İK ve İşe Alım Asistanı",
      desc: "Aday ön eleme, mülakat planlama ve çalışan destek hattı gibi İK süreçlerini otomatize edin.",
      useCases: ["Aday ön eleme görüşmesi", "Mülakat takvimi oluşturma", "İzin ve talep yönetimi", "Oryantasyon bilgilendirmesi"],
    },
  ];

  const hero = (
    <PageHero
      eyebrow="Senaryo Şablonları"
      title="Hazır senaryolarla hemen başlayın"
      subtitle="Sektörünüze ve ihtiyacınıza özel önceden hazırlanmış sesli AI senaryoları. Tek tıkla kopyalayın, özelleştirin, yayına alın."
    />
  );

  return (
    <PageLayout hero={hero}>
      <PageBanner src="/images/pages/workflow.jpg" alt="Senaryo şablonları" height="360px" />
      <SectionReveal>
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {templates.map((tpl) => (
                <div
                  key={tpl.title}
                  className="p-6 rounded-2xl transition-colors duration-200 relative"
                  style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
                >
                  {tpl.popular && (
                    <span
                      className="absolute -top-2.5 right-4 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: "#10b981", color: "#fff" }}
                    >
                      Popüler
                    </span>
                  )}
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(75,111,212,0.12)", color: "var(--brand-cyan)" }}>
                    <tpl.icon size={22} />
                  </div>
                  <h3 className="text-[17px] font-semibold mb-2" style={{ color: "var(--fg)" }}>{tpl.title}</h3>
                  <p className="text-[13.5px] leading-relaxed mb-4" style={{ color: "var(--fg)", opacity: 0.82 }}>{tpl.desc}</p>
                  <div className="mb-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--muted)" }}>Kullanım Alanları</p>
                    <ul className="space-y-1">
                      {tpl.useCases.map((uc) => (
                        <li key={uc} className="text-[12.5px] flex items-center gap-1.5" style={{ color: "var(--fg)", opacity: 0.78 }}>
                          <span className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: "var(--brand-cyan)" }} />{uc}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <a
                    href="https://app.verasist.ai"
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium"
                    style={{ color: "var(--brand-cyan)" }}
                  >
                    Şablonu Kullan <ArrowRight size={14} />
                  </a>
                </div>
              ))}
            </div>
            <div className="mt-12 rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <img
                src="/images/pages/template-design.jpg"
                alt="Şablon tasarımı"
                className="w-full h-auto object-cover"
                style={{ maxHeight: "320px" }}
                loading="lazy"
              />
            </div>
          </div>
        </section>
      </SectionReveal>
    </PageLayout>
  );
};

export default TemplatesPage;
